// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { formatDate, formatDateTime, formatTime } from "@/lib/format";
import { useDateFormat } from "@/lib/preferences";
import { cn } from "@/lib/utils";

type DateInput = string | Date | undefined | null;

interface DateTimeValueProps {
  value: DateInput;
  className?: string;
}

// Date / time / datetime values are rendered in the Cascadia Mono (`font-mono`) face so digits align
// in columns and detail grids. Timezones and durations are intentionally NOT rendered with these.
const VALUE_CLASS = "font-mono tabular-nums";

// There is deliberately no `locale` prop. Date order is a per-viewer preference resolved in one
// place (see `@/lib/format`); letting call sites override it is what previously split the app into
// pages that followed the UI language and pages that followed the host locale.

function EmptyDash() {
  return <span className="text-muted-foreground">—</span>;
}

/** A calendar date (no time component). */
export function DateValue({ value, className }: DateTimeValueProps) {
  const [dateFormat] = useDateFormat();
  if (value == null || value === "") return <EmptyDash />;
  return <span className={cn(VALUE_CLASS, className)}>{formatDate(value, dateFormat)}</span>;
}

/** A time of day (hours:minutes, 24h). */
export function TimeValue({ value, className }: DateTimeValueProps) {
  const [dateFormat] = useDateFormat();
  if (value == null || value === "") return <EmptyDash />;
  return <span className={cn(VALUE_CLASS, className)}>{formatTime(value, dateFormat)}</span>;
}

/** A full date + time. */
export function DateTimeValue({ value, className }: DateTimeValueProps) {
  const [dateFormat] = useDateFormat();
  if (value == null || value === "") return <EmptyDash />;
  return <span className={cn(VALUE_CLASS, className)}>{formatDateTime(value, dateFormat)}</span>;
}
